import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { STORAGE_KEYS } from '../constants/storageKeys.js';

/**
 * 全域購物車狀態。
 *
 * 購物車以「規格(variant)」為單位：每一行用 lineId（= variant id）識別，
 * 所以同一商品的不同規格（21g / 23g）會是分開的兩行，價格各自正確。
 *
 * 暴露：
 *   items        : [{ lineId, productId, variantId, sku, name, price, qty, tags?, image? }, ...]
 *   addToCart(item)        : 加入規格（同 lineId 已存在則 +1）
 *   removeFromCart(lineId)
 *   updateQty(lineId, delta)  delta 可為正負整數
 *   setQty(lineId, qty)       直接設為某數量（最少為 1）
 *   clearCart()
 *   totalCount   : 總件數
 *   subtotal     : 商品小計（不含運費）
 */

const CartContext = createContext(null);

// 取得購物車行的識別：優先用 lineId（variant），舊資料 fallback 到 id
const keyOf = (item) => item.lineId ?? item.id;

// 從 localStorage 還原購物車（重整不丟）；資料壞掉就當空車
function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CART);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  // 購物車異動即持久化
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(items));
    } catch {
      // localStorage 滿了或被停用：購物車退化成單次 session，不影響操作
    }
  }, [items]);

  const addToCart = useCallback((item) => {
    const key = keyOf(item);
    setItems(prev => {
      const exist = prev.find(i => keyOf(i) === key);
      if (exist) return prev.map(i => keyOf(i) === key ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...item, qty: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((lineId) => {
    setItems(prev => prev.filter(i => keyOf(i) !== lineId));
  }, []);

  const updateQty = useCallback((lineId, delta) => {
    setItems(prev => prev.map(i => keyOf(i) === lineId ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  }, []);

  const setQty = useCallback((lineId, qty) => {
    setItems(prev => prev.map(i => keyOf(i) === lineId ? { ...i, qty: Math.max(1, qty) } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalCount = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);

  const value = {
    items, addToCart, removeFromCart, updateQty, setQty, clearCart,
    totalCount, subtotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart 必須在 <CartProvider> 內使用');
  return ctx;
}
